import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Field, Loading, Notice, PageHeader, StatusBadge } from '../components/ui';
import { RichClinicalEditor } from '../components/RichClinicalEditor';
import { api, jsonBody } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCatalogs } from '../lib/hooks';
import type { ClinicalReportTemplate } from '../lib/types';

const emptyHtml = '<p><br></p>';

function htmlToText(html: string) {
  const element = document.createElement('div');
  element.innerHTML = html;
  return (element.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function templateHtml(template: ClinicalReportTemplate | null) {
  const item = template?.contentJson.content.find(
    (value): value is { html: string } =>
      typeof value === 'object' && value !== null && 'html' in value &&
      typeof (value as { html?: unknown }).html === 'string',
  );
  return item?.html || emptyHtml;
}

export function ClinicalTemplateLibraryPage() {
  const { user } = useAuth();
  const catalogs = useCatalogs();
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '', description: '', category: 'Biblioteca clínica', studyTypeId: '', displayOrder: 100,
    active: false, status: 'BORRADOR' as 'BORRADOR' | 'PUBLICADA' | 'INACTIVA', systemTemplate: false, contentKind: 'PLANTILLA_COMPLETA' as 'PLANTILLA_COMPLETA' | 'BLOQUE_CLINICO', html: emptyHtml,
  });
  const canManageSystem = Boolean(user?.roles.some((role) => role === 'ADMINISTRADOR' || role === 'COORDINACION'));

  const templates = useQuery({
    queryKey: ['clinical-report-templates', 'library'],
    queryFn: () => api<ClinicalReportTemplate[]>('/api/clinical-report-templates?includeInactive=true'),
  });
  const selected = templates.data?.find((template) => template.id === selectedId) ?? null;
  const readOnly = Boolean(selected?.systemTemplate && !canManageSystem);

  const loadTemplate = (template: ClinicalReportTemplate | null) => {
    const html = templateHtml(template);
    setSelectedId(template?.id ?? null);
    setForm({
      name: template?.name ?? '',
      description: template?.description ?? '',
      category: template?.category ?? 'Biblioteca clínica',
      studyTypeId: template?.studyTypeId ? String(template.studyTypeId) : '',
      displayOrder: template?.displayOrder ?? 100,
      active: template?.active ?? true,
      status: template?.status ?? 'BORRADOR',
      systemTemplate: template?.systemTemplate ?? false,
      contentKind: template?.contentKind ?? 'PLANTILLA_COMPLETA',
      html,
    });
    setMessage(null);
  };

  const persist = useMutation({
    mutationFn: (payload: typeof form & { id?: number; createNewVersion?: boolean }) => api<ClinicalReportTemplate>('/api/clinical-report-templates', {
      method: 'POST',
      ...jsonBody({
        ...(payload.id ? { id: payload.id } : {}),
        name: payload.name.trim(),
        description: payload.description.trim() || null,
        category: payload.category.trim() || null,
        studyTypeId: payload.studyTypeId ? Number(payload.studyTypeId) : null,
        contentJson: { type: 'doc', content: [{ html: payload.html }] },
        contentText: htmlToText(payload.html),
        contentKind: payload.contentKind,
        status: payload.status,
        createNewVersion: payload.createNewVersion ?? false,
        active: payload.status === 'PUBLICADA',
        systemTemplate: payload.systemTemplate,
        displayOrder: Number(payload.displayOrder),
      }),
    }),
    onSuccess: async (saved) => {
      setMessage('Plantilla clínica guardada.');
      await client.invalidateQueries({ queryKey: ['clinical-report-templates'] });
      await templates.refetch();
      loadTemplate(saved);
      setMessage('Plantilla clínica guardada.');
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'No fue posible guardar la plantilla.'),
  });

  const save = () => {
    persist.mutate({
      ...form,
      ...(selectedId ? { id: selectedId } : {}),
      ...(selected?.status === 'PUBLICADA' || selected?.status === 'INACTIVA'
        ? { createNewVersion: true, status: 'PUBLICADA' as const }
        : {}),
    });
  };

  const publishDraft = () => {
    if (!selected || selected.status !== 'BORRADOR') return;
    persist.mutate({ ...form, id: selected.id, status: 'PUBLICADA' });
  };

  const duplicate = () => {
    if (!selected) return;
    persist.mutate({
      ...form,
      name: `${selected.name} (copia)`,
      active: false,
      status: 'BORRADOR',
      systemTemplate: false,
      html: form.html,
    });
  };

  const toggleActive = () => {
    if (!selected) return;
    persist.mutate({ ...form, id: selected.id, active: false, status: 'INACTIVA' });
  };

  const visible = (templates.data ?? []).filter((template) =>
    filter === 'all' || (filter === 'active' ? template.active : !template.active),
  );

  if (templates.isLoading) return <Loading label="Cargando biblioteca clínica…" />;

  return <>
    <PageHeader title="Biblioteca clínica" description="Plantillas reutilizables para construir borradores médicos sin alterar los datos estructurados del estudio." />
    {message && <Notice kind={message.includes('guardada') ? 'success' : 'error'}>{message}</Notice>}
    <div className="template-library-layout">
      <Card title="Plantillas">
        <div className="stack">
          <div className="action-row">
            <Button type="button" onClick={() => loadTemplate(null)}>Nueva plantilla</Button>
            <select aria-label="Filtrar plantillas" value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
              <option value="active">Activas</option><option value="inactive">Inactivas</option><option value="all">Todas</option>
            </select>
          </div>
          <div className="template-library-list">
            {visible.map((template) => <button key={template.id} type="button" className={template.id === selectedId ? 'selected' : ''} onClick={() => loadTemplate(template)}>
              <span><strong>{template.name}</strong><small>v{template.version} · {template.contentKind === 'BLOQUE_CLINICO' ? 'Bloque clínico' : 'Plantilla completa'} · {template.category || 'Sin categoría'}{template.studyType ? ` · ${template.studyType.name}` : ' · General'}</small></span>
              <span className="template-list-status"><StatusBadge value={template.status} /><small>{template.systemTemplate ? 'Institucional' : `Personal · ${template.createdBy?.displayName || 'Autor identificado'}`}</small></span>
            </button>)}
            {!visible.length && <p className="muted">No hay plantillas para este filtro.</p>}
          </div>
        </div>
      </Card>
      <Card title={selected ? 'Editar plantilla' : 'Nueva plantilla'}>
        <div className="stack">
          {readOnly && <Notice kind="info">Esta plantilla institucional solo puede ser modificada por Coordinación o Administración. Puede duplicarla para crear una variante clínica.</Notice>}
          <div className="form-grid two">
            <Field label="Nombre" required><input disabled={readOnly} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
            <Field label="Categoría"><input disabled={readOnly} value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} /></Field>
            <Field label="Tipo de estudio" hint="Opcional. Sirve para ordenar y sugerir; no impide reutilizarla en otros estudios."><select disabled={readOnly || catalogs.isLoading} value={form.studyTypeId} onChange={(event) => setForm({ ...form, studyTypeId: event.target.value })}><option value="">General</option>{catalogs.data?.studyTypes.filter((item) => item.active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></Field>
            <Field label="Tipo de contenido"><select disabled={readOnly} value={form.contentKind} onChange={(event) => setForm({ ...form, contentKind: event.target.value as typeof form.contentKind })}><option value="PLANTILLA_COMPLETA">Plantilla completa</option><option value="BLOQUE_CLINICO">Bloque clínico</option></select></Field>
            <Field label="Descripción"><input disabled={readOnly} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></Field>
            <Field label="Orden"><input disabled={readOnly} type="number" value={form.displayOrder} onChange={(event) => setForm({ ...form, displayOrder: Number(event.target.value) })} /></Field>
          </div>
          <Field label="Contenido clínico" required hint="Pegue únicamente contenido clínico reutilizable. Las imágenes y los datos de pacientes no forman parte de una plantilla personal.">
            <RichClinicalEditor
              value={form.html}
              editable={!readOnly}
              allowImages={false}
              compact
              onChange={(html) => setForm((current) => ({ ...current, html }))}
              onImageRejected={() => setMessage('Las imágenes pertenecen al informe del caso y no pueden guardarse en una plantilla personal.')}
            />
          </Field>
          <div className="action-row">
            {!readOnly && <Button type="button" disabled={!form.name.trim() || !htmlToText(form.html)} busy={persist.isPending} onClick={save}>{selected?.status === 'PUBLICADA' || selected?.status === 'INACTIVA' ? 'Guardar nueva versión' : 'Guardar borrador'}</Button>}
            {selected?.status === 'BORRADOR' && !readOnly && <Button type="button" busy={persist.isPending} onClick={publishDraft}>Publicar</Button>}
            {selected && <Button type="button" variant="secondary" busy={persist.isPending} onClick={duplicate}>Duplicar</Button>}
            {selected?.status === 'PUBLICADA' && !readOnly && <Button type="button" variant="warning" busy={persist.isPending} onClick={toggleActive}>Desactivar</Button>}
          </div>
        </div>
      </Card>
    </div>
  </>;
}
